import { useCallback, useRef, useState } from 'react';
import { MAX_BULK_VIDEOS } from '../common/model/dto/video/video-constants';
import { CreateVideoRequest } from '../common/model/dto/video/video-requests';
import {
  RejectedVideoFile,
  StagedVideo,
  VideoOrientation,
} from '../common/model/type/video-file.types';
import { VideoPlacementValues } from '../common/model/type/video-placement.types';
import { AddedVideoFiles } from '../common/model/type/video-staging.types';
import { NEW_PLACEMENT_VALUES } from '../common/utils/video-placement-utils';
import {
  contentTypeForFile,
  isSameFile,
  readVideoDimensions,
  readVideoThumbnail,
  titleFromFileName,
  toCreateVideoRequest,
  validateVideoFile,
} from '../common/utils/video-file-utils';

export function useVideoStaging() {
  const [staged, setStaged] = useState<StagedVideo[]>([]);
  const [rejected, setRejected] = useState<RejectedVideoFile[]>([]);
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const nextKey = useRef(0);

  const update = useCallback(
    (key: string, changes: Partial<StagedVideo>) =>
      setStaged((items) =>
        items.map((item) => (item.key === key ? { ...item, ...changes } : item))
      ),
    []
  );

  const addFiles = (files: File[]): AddedVideoFiles => {
    const accepted: StagedVideo[] = [];
    const rejections: RejectedVideoFile[] = [];
    const repeated: string[] = [];

    files.forEach((file) => {
      if (
        [...staged, ...accepted].some((item) => isSameFile(item.file, file))
      ) {
        repeated.push(file.name);

        return;
      }

      const key = String(nextKey.current++);
      const contentType = contentTypeForFile(file);
      const rejection =
        validateVideoFile(file) ??
        (staged.length + accepted.length >= MAX_BULK_VIDEOS ? 'tooMany' : null);

      if (rejection || !contentType) {
        rejections.push({
          key,
          name: file.name,
          reason: rejection ?? 'unsupportedType',
        });

        return;
      }

      accepted.push({
        key,
        file,
        contentType,
        title: titleFromFileName(file.name),
        description: '',
        dimensions: 'reading',
        thumbnail: 'reading',
        orientation: null,
        placement: NEW_PLACEMENT_VALUES,
      });
    });

    setRejected(rejections);
    setDuplicates(repeated);
    setStaged((items) => [...items, ...accepted]);
    accepted.forEach(({ key, file }) => {
      readVideoDimensions(file).then((dimensions) =>
        update(key, { dimensions })
      );
      readVideoThumbnail(file).then((thumbnail) => update(key, { thumbnail }));
    });

    return { added: accepted.length };
  };

  const setTitle = (key: string, title: string) => update(key, { title });

  const setDescription = (key: string, description: string) =>
    update(key, { description });

  const setOrientation = (key: string, orientation: VideoOrientation) =>
    update(key, { orientation });

  const setPlacement = (key: string, changes: Partial<VideoPlacementValues>) =>
    setStaged((items) =>
      items.map((item) =>
        item.key === key
          ? { ...item, placement: { ...item.placement, ...changes } }
          : item
      )
    );

  const remove = (key: string) =>
    setStaged((items) => items.filter((item) => item.key !== key));

  const reset = () => {
    setStaged([]);
    setRejected([]);
    setDuplicates([]);
  };

  const requests = staged
    .map(toCreateVideoRequest)
    .filter((request): request is CreateVideoRequest => request !== null);

  return {
    staged,
    rejected,
    duplicates,
    requests,
    addFiles,
    setTitle,
    setDescription,
    setOrientation,
    setPlacement,
    remove,
    reset,
  };
}
